/* eslint-disable indent */
import React, {useEffect, useState} from 'react';
import {Modal, Row, Col} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';
import {isEqual, isObject} from 'lodash';
import {GreenButton, WhiteButton} from '@styled';
import {Input, Form, MainLoader, DropdownField, Checkbox} from '@components';
import {useDispatch, useSelector} from 'react-redux';
import {SYSTEM_OPERATORS} from '@app/utils/constants';
import {sagaActions} from './redux/saga';
import {resetFormSubmitSuccess, resetFormError} from './redux/index';

const AddEditInputData = ({show, sourceId, editRow, toggleModal}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState('add');
  const [duplicateError, setDuplicateError] = useState('');
  const inputFieldsData = useSelector(
    (state) => state.calcEngine.sourceInputFields.data
  );

  const remoteUnits = useSelector(
    (state) => state.calcEngine.sourceInputFields.units
  );

  const remoteData = useSelector(
    (state) => state.calcEngine.sourceInputData.data
  );

  const isLoading = useSelector((state) => state.calcEngine.formLoading);
  const formError = useSelector((state) => state.calcEngine.formError);
  const formSubmitSuccess = useSelector(
    (state) => state.calcEngine.formSubmitSuccess
  );

  useEffect(() => {
    if (formSubmitSuccess) {
      dispatch(resetFormSubmitSuccess(false));
      toggleModal(false);
    }
  }, [formSubmitSuccess]);

  useEffect(() => {
    setEditMode(editRow && !editRow.duplicate ? 'update' : 'add');
  }, [editRow]);

  useEffect(() => {
    dispatch(resetFormError(''));
  }, [show]);

  const handleSubmit = (values) => {
    // validate already exists with all key combinations
    const rowData = {};
    const {
      standardUnit,
      unitConvOperator,
      unitConvField,
      inputQuantity,
      ...restData
    } = values;
    Object.keys(restData).map((itemKey) => {
      if (isObject(values[itemKey])) {
        rowData[itemKey] = values[itemKey].value;
      } else {
        rowData[itemKey] = values[itemKey];
      }
      return itemKey;
    });
    const foundDataItem = remoteData.find((itemData) => {
      return isEqual(itemData, rowData);
    });

    if (foundDataItem) {
      setDuplicateError(
        'Data record with same values combination already exists'
      );
      return;
    }

    dispatch({
      type:
        editMode === 'update'
          ? sagaActions.UPDATE_INPUT_TABLE_DATA
          : sagaActions.CREATE_INPUT_TABLE_DATA,
      payload: {
        sourceId,
        dataId: editRow && !editRow.duplicate ? editRow.id : 0,
        values: {
          data: JSON.stringify(rowData),
          standardUnit: values.standardUnit.value,
          conversionEq: inputQuantity
            ? `${inputQuantity}.${unitConvOperator.value}.${unitConvField.value}`
            : '',
          applySubEquation: values.applySubEquation.length ? true : false,
        },
      },
    });
  };

  const systemUnits = React.useMemo(() => {
    return remoteUnits.map((item) => ({value: item.id, label: item.name}));
  }, [remoteUnits]);

  const {initialFormValues, formFields, nonStandardFields} =
    React.useMemo(() => {
      const formValues = {};
      const localFields = [];
      const localNonStandardFields = [];
      // validationFields
      // const localFieldValidations = {};
      if (inputFieldsData.length) {
        inputFieldsData.map((item) => {
          formValues[item.id] = editRow ? editRow.data[item.id] : '';
          if (item.fieldType === 'Unit') {
            formValues[item.id] = editRow
              ? systemUnits.find(
                  (unit) => unit.value === Number(editRow.data[item.id])
                ) || ''
              : '';
          }
          localFields.push({
            name: item.id,
            label: item.fieldName,
            type: item.fieldType,
            level: item.fieldLevel,
          });

          if (item.calculationField === 'Non-Standard-Unit') {
            localNonStandardFields.push({
              value: item.id,
              label: item.fieldName,
            });
          }

          // localFieldValidations[item.id] =
          //   Yup.string().required('Field Required');
          return item;
        });
        formValues.standardUnit = editRow
          ? systemUnits.find(
              (unit) => unit.value === Number(editRow.standardUnit)
            ) || ''
          : '';
        // conversion equation
        const conversionEq = (editRow ? editRow.conversionEq : '').split('.');
        formValues.inputQuantity = conversionEq.length ? conversionEq[0] : '';
        formValues.unitConvOperator = conversionEq.length
          ? SYSTEM_OPERATORS.find((op) => op.value === conversionEq[1]) || ''
          : '';
        formValues.unitConvField = conversionEq.length
          ? localNonStandardFields.find(
              (field) => field.value === Number(conversionEq[2])
            ) || ''
          : '';

        formValues.applySubEquation =
          editRow && editRow.applySubEquation ? ['1'] : [];
      }

      return {
        initialFormValues: formValues,
        formFields: localFields,
        nonStandardFields: localNonStandardFields,
      };
    }, [inputFieldsData, editRow]);

  return (
    <Modal
      show={show}
      onHide={() => toggleModal(false)}
      centered
      // scrollable
      keyboard={false}
      backdrop="static"
    >
      <Modal.Header closeButton>
        <h6>{t(`owner.calcEngine.inputTableData.${editMode}`)}</h6>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <MainLoader
            minHeight="100vh"
            spinnerColor="#479541"
            background="rgba(0,0,0,0.2)"
          />
        )}
        <Form
          initialValues={{
            ...initialFormValues,
            inputQuantity: 'Input Quantity',
          }}
          validationSchema={Yup.object({
            // ...validationFields,
          })}
          onSubmit={(values) => {
            handleSubmit(values);
          }}
          enableReinitialize={editMode === 'update' ? true : false}
        >
          {formError && <div className="alert alert-danger">{formError}</div>}
          {duplicateError && (
            <div className="alert alert-danger">{duplicateError}</div>
          )}
          {formFields.map((field) => {
            return (
              <Row key={`field-${field.name}`}>
                <Col>
                  {field.type === 'Unit' ? (
                    <DropdownField
                      id={field.name}
                      name={field.name}
                      label={field.label}
                      options={systemUnits}
                      placeholder=""
                      // required
                      menuPortalTarget="parent"
                    />
                  ) : (
                    <Input
                      type="text"
                      name={field.name}
                      id={field.name}
                      label={field.label}
                      placeholder=""
                      // required
                    />
                  )}
                </Col>
              </Row>
            );
          })}
          <Row>
            <Col>
              <DropdownField
                id="standardUnit"
                name="standardUnit"
                label="Standard Unit"
                options={systemUnits}
                placeholder=""
                // required
                menuPortalTarget="parent"
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Checkbox
                id="applySubEquation"
                name="applySubEquation"
                label="Apply sub equation if defined"
                fieldValue="1"
              />
            </Col>
          </Row>
          {nonStandardFields.length ? (
            <>
              <Row>
                <Col>
                  <span>Unit Conversion For Non Standard</span>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Input
                    type="text"
                    name="inputQuantity"
                    id="inputQuantity"
                    label="Input Quantity"
                    placeholder=""
                    disabled
                  />
                </Col>
                <Col>
                  <DropdownField
                    id="unitConvOperator"
                    name="unitConvOperator"
                    label="Operator"
                    options={SYSTEM_OPERATORS}
                    placeholder=""
                    // required
                    menuPortalTarget="parent"
                  />
                </Col>
                <Col>
                  <DropdownField
                    id="unitConvField"
                    name="unitConvField"
                    label="Non Standard Unit Field"
                    options={nonStandardFields}
                    placeholder=""
                    // required
                    menuPortalTarget="parent"
                  />
                </Col>
              </Row>
            </>
          ) : null}
          <div
            style={{
              visibility: 'hidden',
              width: 0,
              lineHeight: 0,
              padding: 0,
              margin: 0,
              fontSize: 0,
            }}
          >
            <GreenButton id="mInputDataSubmitBtn" type="submit">
              {t(`owner.calcEngine.inputTableData.${editMode}BtnLabel`)}
            </GreenButton>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <GreenButton
          type="button"
          className="mr-1"
          onClick={() => {
            document.getElementById('mInputDataSubmitBtn').click();
          }}
        >
          {t(`owner.calcEngine.inputTableData.${editMode}BtnLabel`)}
        </GreenButton>
        <WhiteButton
          type="button"
          className="ml-1"
          onClick={() => toggleModal(false)}
        >
          {t('owner.calcEngine.inputTableData.cancelBtnLabel')}
        </WhiteButton>
      </Modal.Footer>
    </Modal>
  );
};

export default AddEditInputData;
